import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { TriangleAlert } from "lucide-react";
import { captureError } from "@ticketnjoy/monitoring";

import { CenteredMessage } from "@/components/centered-message";
import { Button } from "@/components/ui/button";

class RenderErrorBoundary extends Component<
  { resetKey: string; fallback: ReactNode; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Unhandled render error", error, info.componentStack);
    captureError(error, { componentStack: info.componentStack });
  }

  componentDidUpdate(prev: { resetKey: string }) {
    if (this.state.failed && prev.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

/** Beyaz ekran yerine kurtarma kartı; başka sayfaya geçince kendini sıfırlar. */
export function AppErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <RenderErrorBoundary resetKey={pathname} fallback={<ErrorFallback />}>
      {children}
    </RenderErrorBoundary>
  );
}

function ErrorFallback() {
  return (
    <CenteredMessage
      tone="danger"
      icon={<TriangleAlert className="h-7 w-7" />}
      title="Bir şeyler ters gitti"
      description="Bu sayfa beklenmedik bir hata verdi. Sayfayı yenilemek çoğu zaman çözer; sorun sürerse teknik ekibe haber ver."
    >
      <Button onClick={() => window.location.reload()}>Sayfayı yenile</Button>
      <Button variant="outline" asChild>
        <Link to="/">Genel bakışa dön</Link>
      </Button>
    </CenteredMessage>
  );
}
